import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import {
  DeliveryOption,
  EnrichedSupplierForDelivery,
  EstimateSplitDeliveryType,
} from "../types";
import { calculateDiscountedPrice } from "./deliveryCostUtils";
import { getEffectiveDeliveryCost } from "./vehicleTypeUtils";
import {
  CPTSlabForManualOption,
  EstimateSplitFulfillmentStatus,
} from "../../../views/Orders/enums";

dayjs.extend(utc);
dayjs.extend(timezone);

const IST_TIMEZONE = "Asia/Kolkata";

/** FASTEST → cptSlab 1, CHEAPEST → cptSlab 2 */
export function getCptSlabForDeliveryType(
  deliveryType: EstimateSplitDeliveryType,
): CPTSlabForManualOption {
  return deliveryType === "CHEAPEST"
    ? CPTSlabForManualOption.STANDARD_DELIVERY
    : CPTSlabForManualOption.INSTANT_DELIVERY;
}

export interface EstimateMaxBounds {
  /**
   * FASTEST: sum of each FULFILLED split's max discounted cptSlab-1 cost.
   * CHEAPEST: max of each FULFILLED split's max discounted cptSlab-2 cost.
   */
  deliveryCostMin: number;
  /**
   * Latest cptMax among matching CPT-slab options on FULFILLED splits (ISO UTC).
   */
  latestOptionCptMaxIso: string | null;
  targetCptSlab: number;
}

function getActiveDeliveryOptions(
  supplier: EnrichedSupplierForDelivery,
): DeliveryOption[] {
  return (supplier.deliveryOptions ?? []).filter(
    (option) => !(option as { isDeleted?: boolean }).isDeleted,
  );
}

function getActiveOptionsForSlab(
  supplier: EnrichedSupplierForDelivery,
  targetSlab: number,
): DeliveryOption[] {
  return getActiveDeliveryOptions(supplier).filter(
    (option) => Number(option.cptSlab) === targetSlab,
  );
}

export function computeEstimateMaxBounds(
  suppliers: EnrichedSupplierForDelivery[],
  deliveryType: EstimateSplitDeliveryType = "FASTEST",
): EstimateMaxBounds {
  const targetCptSlab = getCptSlabForDeliveryType(deliveryType);
  const supplierSplits = suppliers.filter(
    (supplier) =>
      supplier.supplierId != null &&
      supplier.fulfillmentStatus === EstimateSplitFulfillmentStatus.FULFILLED,
  );

  const perSplitMaxCosts = supplierSplits
    .map((supplier) => {
      const options = getActiveOptionsForSlab(supplier, targetCptSlab);
      if (options.length === 0) {
        return null;
      }
      return Math.max(
        ...options.map((option) => {
          const cost =
            option.deliveryCost == null
              ? 0
              : Number(
                  getEffectiveDeliveryCost(
                    option,
                    supplier.selectedVehicleType,
                  ),
                );
          return calculateDiscountedPrice(
            cost,
            option.deliveryCostDiscountPercentage,
          );
        }),
      );
    })
    .filter((cost): cost is number => cost != null);

  const deliveryCostMin =
    perSplitMaxCosts.length === 0
      ? 0
      : deliveryType === "CHEAPEST"
        ? Math.max(...perSplitMaxCosts)
        : perSplitMaxCosts.reduce((sum, cost) => sum + cost, 0);

  let latestMs: number | null = null;
  for (const supplier of supplierSplits) {
    for (const option of getActiveOptionsForSlab(supplier, targetCptSlab)) {
      if (!option.cptMax) continue;
      const ms = dayjs(option.cptMax).valueOf();
      if (Number.isNaN(ms)) continue;
      if (latestMs == null || ms > latestMs) {
        latestMs = ms;
      }
    }
  }

  return {
    deliveryCostMin,
    latestOptionCptMaxIso:
      latestMs == null ? null : dayjs(latestMs).toISOString(),
    targetCptSlab,
  };
}

export interface EstimateMaxBoundsValidation {
  isCptValid: boolean;
  isCostValid: boolean;
  /** True when empty or when filled values satisfy option bounds. */
  isValid: boolean;
  cptError: string | null;
  costError: string | null;
  bounds: EstimateMaxBounds;
}

/**
 * Validate ops-entered Max CPT / Max Delivery Cost for a delivery type.
 * Only FULFILLED supplier splits contribute to CPT / cost bounds.
 * - FASTEST: Max CPT >= all cptSlab=1 option CPT times on those splits
 * - CHEAPEST: Max CPT >= all cptSlab=2 option CPT times on those splits
 * - Max Delivery Cost (when set) must be >= 0
 * Empty values are allowed (fields are optional).
 * `maxCptLocal` is datetime-local IST (YYYY-MM-DDTHH:mm).
 */
export function validateEstimateMaxBounds(
  suppliers: EnrichedSupplierForDelivery[],
  maxCptLocal: string,
  maxDeliveryCost: number | undefined,
  deliveryType: EstimateSplitDeliveryType = "FASTEST",
): EstimateMaxBoundsValidation {
  const bounds = computeEstimateMaxBounds(suppliers, deliveryType);

  let cptError: string | null = null;
  let isCptValid = true;
  if (maxCptLocal) {
    const maxCptUtc = dayjs.tz(maxCptLocal, IST_TIMEZONE).utc();
    if (!maxCptUtc.isValid()) {
      cptError = "Enter a valid Max CPT Time";
      isCptValid = false;
    } else if (
      bounds.latestOptionCptMaxIso &&
      maxCptUtc.valueOf() < dayjs(bounds.latestOptionCptMaxIso).valueOf()
    ) {
      cptError = `Max CPT Time must be greater than or equal to all cptSlab ${bounds.targetCptSlab} CPT times for ${deliveryType}`;
      isCptValid = false;
    }
  }

  let costError: string | null = null;
  let isCostValid = true;
  if (maxDeliveryCost !== undefined && maxDeliveryCost !== null) {
    if (Number.isNaN(Number(maxDeliveryCost)) || Number(maxDeliveryCost) < 0) {
      costError = "Max Delivery Cost must be at least ₹0";
      isCostValid = false;
    }
  }

  return {
    isCptValid,
    isCostValid,
    isValid: isCptValid && isCostValid,
    cptError,
    costError,
    bounds,
  };
}

export function toEstimateCptMaxUtcIso(maxCptLocal: string): string {
  return dayjs.tz(maxCptLocal, IST_TIMEZONE).utc().toISOString();
}

export function toEstimateCptMaxLocal(isoUtc?: string | null): string {
  if (!isoUtc) return "";
  const local = dayjs(isoUtc).tz(IST_TIMEZONE);
  return local.isValid() ? local.format("YYYY-MM-DDTHH:mm") : "";
}
